<template>
  <div class="chart-wrapper-page">
    <div class="beta-banner">
      <span class="beta-tag">BETA</span>
      <span>
        New and still being refined — the model and the numbers can change as we validate them. Found something
        that looks wrong? Say so, that's exactly what this stage is for.
      </span>
    </div>

    <p class="intro">
      Built from <code>customer_month_subscriptions</code> — one row per customer, per brand, per month, holding the
      number of recurring subscriptions billed and the recurring MRR. Rebuilt from issued invoices; it is not the
      live order state. <strong>Run-rate</strong> figures below include <em>carry-forward</em>: a customer between
      invoices (e.g. billed once a year) keeps its subscriptions through the gap months instead of dropping to zero
      and back. <strong>Invoiced only</strong> figures count a month only if an invoice actually covers it.
      Dashed / faded = forward projection (current fleet held flat), never counted as churn.
      <code>national_netherlands</code> is excluded. The first chart also overlays <strong>billed IMEIs</strong>
      (physical devices on the invoices) and <strong>vehicles in the fleet portal</strong> for the billed-vs-deployed picture.
    </p>

    <p class="click-hint">
      <span class="hint-icon" aria-hidden="true">👆</span>
      <strong>Anything with a pointer cursor is clickable:</strong> a bar in a chart or a year in the reconciliation
      table opens the customers behind it; a column header in one of those pop-ups sorts by that column.
    </p>

    <div class="brand-bar">
      <label>Brand</label>
      <select v-model="brand" @change="fetchData">
        <option v-for="b in brandOptions" :key="b.value" :value="b.value">{{ b.label }}</option>
      </select>
    </div>

    <!-- Stat row -->
    <div class="stat-row">
      <div class="stat-card">
        <span class="stat-label">Subscriptions</span>
        <span class="stat-value">{{ fmtInt(latest.devices) }}</span>
        <span class="stat-delta" :class="deltaClass(latest.devicesDelta)">{{ fmtDelta(latest.devicesDelta, 0) }} vs prev month</span>
        <span class="stat-sub">SUM(device_count), latest closed month. Billed quantity of recurring lines.</span>
      </div>
      <div class="stat-card">
        <span class="stat-label">MRR / month</span>
        <span class="stat-value">{{ fmtEur(latest.mrr) }}</span>
        <span class="stat-delta" :class="deltaClass(latest.mrrDelta)">{{ fmtDelta(latest.mrrDelta, 0, true) }} vs prev month</span>
        <span class="stat-sub">SUM(mrr_cents)/100 for invoice + carry-forward rows.</span>
      </div>
      <div class="stat-card">
        <span class="stat-label">ARR (run-rate)</span>
        <span class="stat-value">{{ fmtEur(latest.mrr * 12) }}</span>
        <span class="stat-sub">MRR / month &times; 12. See the reconciliation below for why this differs from the Overview tile.</span>
      </div>
      <div class="stat-card">
        <span class="stat-label">Price / subscription</span>
        <span class="stat-value">{{ fmtEur(latest.arpu, 2) }}</span>
        <span class="stat-delta" :class="deltaClass(latest.arpuDelta)">{{ fmtDelta(latest.arpuDelta, 2, true) }} vs prev month</span>
        <span class="stat-sub">MRR &divide; Subscriptions (ARPU). Low if a device bills several recurring lines.</span>
      </div>
    </div>

    <!-- ARR reconciliation -->
    <div class="chart-card">
      <div class="chart-header"><h3 class="chart-title">ARR: this table vs the Overview tile</h3></div>
      <div class="explain">
        The Overview <strong>“ARR Start of Year”</strong> is
        <code>SUM(invoice_schedules.year_start_total_recurring)</code> for schedules created before 1 January and
        scheduled to bill during that year (<code>arrStartOfYearWithChurn</code>). It differs from this table on four axes:
        <ul>
          <li><strong>Instant</strong> — Overview is taken on 1 January; the headline stat above is the latest closed month.</li>
          <li><strong>Source</strong> — Overview reads a cached figure on the schedule row; this table sums actual issued invoices.</li>
          <li><strong>Carry-forward</strong> — this table's run-rate adds customers between invoices; the “invoiced only” column does not.</li>
          <li><strong>Projection</strong> — for a year not yet billed (2027), the comparable figure is this table's <em>projection</em> (current fleet held flat), since almost nothing is invoiced yet. Past years use run-rate.</li>
          <li><strong>Scope</strong> — <code>national_netherlands</code> is excluded on both sides (it runs no recurring subscription billing here); Overview's future years also undercount where forward schedule rows are not generated yet.</li>
        </ul>
        <strong>Click a year</strong> for the customer-by-customer breakdown of the gap, to the euro.
      </div>
      <div v-if="isLoading" class="loading-state small"><icon name="spinner" spin class="spinner"></icon></div>
      <table v-else class="recon-table">
        <thead>
          <tr>
            <th>Year (1 Jan)</th>
            <th>Invoiced only</th>
            <th>+ carry-forward = run-rate</th>
            <th>+ projection</th>
            <th>Overview “ARR Start of Year”</th>
            <th>This − Overview</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="y in recon.years" :key="y.year" class="recon-row" @click="openReconYear(y.year)">
            <td>{{ y.year }}<span v-if="y.future" class="tag">projected</span></td>
            <td>{{ y.future ? '—' : fmtEur(y.table_invoiced_arr) }}</td>
            <td>{{ y.future ? '—' : fmtEur(y.table_runrate_arr) }}<span v-if="!y.future" class="muted"> (+{{ fmtEur(y.carry_forward_part) }})</span></td>
            <td>{{ fmtEur(y.table_projected_arr) }}<span class="muted"> (+{{ fmtEur(y.projection_part) }})</span></td>
            <td>{{ fmtEur(y.overview_arr) }}</td>
            <td :class="deltaClass(y.vs_overview)">{{ fmtDelta(y.vs_overview, 0, true) }}</td>
          </tr>
        </tbody>
      </table>
      <p v-if="!isLoading" class="chart-hint">
        Headline stat ARR (run-rate) = {{ fmtEur(recon.current && recon.current.runrate_arr) }} at {{ recon.current && recon.current.month }};
        invoiced-only at the same month = {{ fmtEur(recon.current && recon.current.invoiced_arr) }}.
      </p>
    </div>

    <!-- Subscriptions -->
    <div class="chart-card">
      <div class="chart-header">
        <h3 class="chart-title">Subscriptions over time</h3>
        <div class="year-filters">
          <button v-for="opt in rangeOptions" :key="opt.months"
            :class="['year-btn', { active: rangeMonths === opt.months }]"
            @click="setRange(opt.months)">{{ opt.label }}</button>
        </div>
      </div>
      <div class="explain">
        Bars = <code>SUM(device_count)</code> of live rows (billed subscription units). Solid = billed or
        carried-forward; faded = forward projection (each entity's last actual fleet held flat while it still has a
        live order).
        <strong>Billed IMEIs</strong> = distinct IMEIs on the covering invoices (<code>billing_invoice_devices</code>) —
        low for older months, that table fills only recently.
        <strong>In fleet portal</strong> = distinct vehicles the customers had in the ulu fleet app that month
        (<code>vehicle_company_permissions</code>). The gap between billed units and portal vehicles is the “ghost”
        fleet — running but not invoiced.
        <strong>Click a bar</strong> to see which customers changed that month and by how much.
      </div>
      <div class="chart-container">
        <div v-if="isLoading" class="loading-state"><icon name="spinner" spin class="spinner"></icon><p>Loading…</p></div>
        <canvas v-show="!isLoading" ref="devChart"></canvas>
      </div>
    </div>

    <!-- MRR + ARPU -->
    <div class="chart-card">
      <div class="chart-header"><h3 class="chart-title">MRR &amp; price per subscription</h3></div>
      <div class="explain">
        <strong>MRR / month €</strong> = <code>SUM(mrr_cents)/100</code> for invoice + carry-forward rows (solid), then
        projection (dashed). <strong>Invoiced only €</strong> = invoice rows only — strict billing coverage, the number
        the legacy ARR benchmark matches; the gap to MRR is the carry-forward contribution.
        <strong>Price / subscription €</strong> (right axis) = MRR &divide; Subscriptions.
      </div>
      <div class="chart-container">
        <div v-if="isLoading" class="loading-state"><icon name="spinner" spin class="spinner"></icon><p>Loading…</p></div>
        <canvas v-show="!isLoading" ref="mrrChart"></canvas>
      </div>
    </div>

    <!-- Price movement -->
    <div class="chart-card">
      <div class="chart-header"><h3 class="chart-title">Price per subscription — movement</h3></div>
      <div class="explain">
        <strong>Price / subscription €</strong> (line) and its <strong>month-over-month change</strong> (bars).
        Rising bars = price increases across the base; falling = price erosion or mix shift toward cheaper subscriptions.
        This is the same signal as “Rate Δ” in the waterfall below, expressed per subscription instead of in euros.
      </div>
      <div class="chart-container">
        <div v-if="isLoading" class="loading-state"><icon name="spinner" spin class="spinner"></icon><p>Loading…</p></div>
        <canvas v-show="!isLoading" ref="priceChart"></canvas>
      </div>
    </div>

    <!-- MRR waterfall -->
    <div class="chart-card">
      <div class="chart-header"><h3 class="chart-title">MRR movement &amp; churn</h3></div>
      <div class="explain">
        Each month's change in MRR, split so the parts sum exactly to <strong>Net ΔMRR</strong> (line):
        <ul>
          <li><strong>New logo</strong> — a customer's first-ever activation. <code>prev MRR = 0</code>, this is its earliest active month.</li>
          <li><strong>Reactivation</strong> — a previously-churned customer returns. <code>prev MRR = 0</code> but it was active before.</li>
          <li><strong>Expansion / Contraction</strong> — continuing customer, more / fewer subscriptions:
              <code>Δsubs &times; previous price per subscription</code>.</li>
          <li><strong>Rate Δ</strong> — continuing customer, price per subscription changed: <code>ΔMRR &minus; the volume part</code>.</li>
          <li><strong>Churned</strong> — customer dropped to zero. <code>−(previous MRR)</code>.</li>
          <li><strong>Other</strong> — customer whose implied price per subscription is outside €0.01–€2000 (bundled line items); its net ΔMRR is shown here and kept out of the volume/rate split so it can't distort it.</li>
        </ul>
        Orange line (right axis) = <strong>churn rate %</strong> = churned MRR &divide; prior-month MRR.
        <strong>Click a segment</strong> to list the customers behind it.
      </div>
      <div class="chart-container">
        <div v-if="isLoading" class="loading-state"><icon name="spinner" spin class="spinner"></icon><p>Loading…</p></div>
        <canvas v-show="!isLoading" ref="bridgeChart"></canvas>
      </div>
    </div>

    <!-- Sales -->
    <div class="chart-card">
      <div class="chart-header"><h3 class="chart-title">Sales — subscriptions added &amp; lost</h3></div>
      <div class="explain">
        Subscription <em>count</em> movements per month (not euros):
        <strong>New logo</strong> and <strong>Reactivation</strong> are net-new customers vs won-back customers;
        <strong>Expansion</strong> is existing customers adding subscriptions (resell). Below the line:
        <strong>Contraction</strong> and <strong>Churned</strong>. Use it to see whether growth is coming from new
        business or from selling more into the existing base.
      </div>
      <div class="chart-container">
        <div v-if="isLoading" class="loading-state"><icon name="spinner" spin class="spinner"></icon><p>Loading…</p></div>
        <canvas v-show="!isLoading" ref="salesChart"></canvas>
      </div>
    </div>

    <!-- Movement drill-down modal -->
    <b-modal centered size="xl" :visible="showModal" hide-footer title-tag="div" @hide="showModal = false">
      <template #modal-title>
        {{ modalTitle }} <span class="modal-count">({{ modalRows.length }})</span>
      </template>
      <div v-if="modalLoading" class="modal-loading"><icon name="spinner" spin class="spinner"></icon><p>Loading…</p></div>
      <div v-else-if="modalRows.length === 0" class="modal-empty">No customer changes for this selection.</div>
      <table v-else class="customer-list-table">
        <thead>
          <tr>
            <th @click="sortModal('company_name')">Company <span class="sort">{{ sortArrow('company_name') }}</span></th>
            <th @click="sortModal('brand')">Brand <span class="sort">{{ sortArrow('brand') }}</span></th>
            <th @click="sortModal('kind')">Kind <span class="sort">{{ sortArrow('kind') }}</span></th>
            <th class="num" @click="sortModal('cur_devices')">Subs <span class="sort">{{ sortArrow('cur_devices') }}</span></th>
            <th class="num" @click="sortModal('dev_delta')">Δ Subs <span class="sort">{{ sortArrow('dev_delta') }}</span></th>
            <th class="num" @click="sortModal('cur_mrr')">MRR <span class="sort">{{ sortArrow('cur_mrr') }}</span></th>
            <th class="num" @click="sortModal('mrr_delta')">Δ MRR <span class="sort">{{ sortArrow('mrr_delta') }}</span></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in sortedModalRows" :key="r.customer_id + '-' + r.brand">
            <td>
              <router-link :to="{ name: 'customer', params: { id: r.customer_id } }">{{ r.company_name || ('#' + r.customer_id) }}</router-link>
            </td>
            <td>{{ r.brand }}</td>
            <td><span class="kind-badge" :class="r.kind">{{ r.kind.replace('_', ' ') }}</span></td>
            <td class="num">{{ r.prev_devices }} → {{ r.cur_devices }}</td>
            <td class="num" :class="deltaClass(r.dev_delta)">{{ fmtDelta(r.dev_delta, 0) }}</td>
            <td class="num">{{ fmtEur(r.prev_mrr) }} → {{ fmtEur(r.cur_mrr) }}</td>
            <td class="num" :class="deltaClass(r.mrr_delta)">{{ fmtDelta(r.mrr_delta, 0, true) }}</td>
          </tr>
        </tbody>
      </table>
    </b-modal>

    <!-- ARR reconciliation drill-down -->
    <b-modal centered size="xl" :visible="showRecon" hide-footer title-tag="div" @hide="showRecon = false">
      <template #modal-title>
        ARR gap by customer · 1 Jan {{ reconYear }} <span class="modal-count">({{ reconRows.length }})</span>
      </template>
      <div v-if="reconLoading" class="modal-loading"><icon name="spinner" spin class="spinner"></icon><p>Loading…</p></div>
      <div v-else-if="reconRows.length === 0" class="modal-empty">No customers for this year.</div>
      <table v-else class="customer-list-table">
        <thead>
          <tr>
            <th @click="sortRecon('company_name')">Company <span class="sort">{{ sortArrow('company_name', 'recon') }}</span></th>
            <th class="num" @click="sortRecon('this_arr')">This table ARR <span class="sort">{{ sortArrow('this_arr', 'recon') }}</span></th>
            <th class="num" @click="sortRecon('overview_arr')">Overview ARR <span class="sort">{{ sortArrow('overview_arr', 'recon') }}</span></th>
            <th class="num" @click="sortRecon('delta')">Difference <span class="sort">{{ sortArrow('delta', 'recon') }}</span></th>
            <th @click="sortRecon('only_in')">Only in <span class="sort">{{ sortArrow('only_in', 'recon') }}</span></th>
            <th class="why">Why the gap</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in sortedReconRows" :key="r.customer_id">
            <td>
              <router-link :to="{ name: 'customer', params: { id: r.customer_id } }">{{ r.company_name || ('#' + r.customer_id) }}</router-link>
            </td>
            <td class="num">{{ fmtEur(r.this_arr) }}</td>
            <td class="num">{{ fmtEur(r.overview_arr) }}</td>
            <td class="num" :class="deltaClass(r.delta)">{{ fmtDelta(r.delta, 0, true) }}</td>
            <td><span v-if="r.only_in !== 'both'" class="tag">{{ r.only_in === 'this_table' ? 'this table' : 'Overview' }}</span></td>
            <td class="why">{{ r.reason }}</td>
          </tr>
        </tbody>
      </table>
    </b-modal>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import Chart from "chart.js";
import "vue-awesome/icons/spinner";

export default {
  components: { Icon: () => import("vue-awesome/components/Icon.vue") },
  data() {
    return {
      isLoading: false,
      rangeMonths: 24,
      rangeOptions: [
        { months: 12, label: "12m" },
        { months: 24, label: "24m" },
        { months: 36, label: "36m" }
      ],
      brand: "all",
      brandOptions: [
        { value: "all", label: "All brands" },
        { value: "cartracker", label: "Cartracker" },
        { value: "traksi", label: "Traksi" },
        { value: "ulu", label: "Ulu" },
        { value: "fleetify", label: "Fleetify" },
        { value: "uluuk", label: "Ulu UK" },
        { value: "requre", label: "Requre" },
        { value: "imoto", label: "iMoto" }
      ],
      series: { labels: [], datasets: [], meta: {} },
      bridge: { labels: [], mrr: { datasets: [] }, counts: { datasets: [] } },
      recon: { current: null, years: [] },
      charts: {},
      showModal: false,
      modalLoading: false,
      modalTitle: "",
      modalRows: [],
      modalSort: { key: "mrr_delta", dir: -1 },
      showRecon: false,
      reconLoading: false,
      reconYear: null,
      reconRows: [],
      reconSort: { key: "delta", dir: -1 }
    };
  },
  computed: {
    sortedModalRows() { return this.sortRows(this.modalRows, this.modalSort); },
    sortedReconRows() { return this.sortRows(this.reconRows, this.reconSort); },
    latest() {
      const s = this.series;
      const idx = s.meta && s.meta.firstProjectedIndex != null
        ? s.meta.firstProjectedIndex - 1 : s.labels.length - 1;
      const at = (label, i) => {
        const d = s.datasets.find(x => x.label === label);
        return d ? d.data[i] : null;
      };
      const dev = at("Subscriptions", idx) || 0;
      const devP = at("Subscriptions", idx - 1) || 0;
      const mrr = at("MRR / month €", idx) || 0;
      const mrrP = at("MRR / month €", idx - 1) || 0;
      const arpu = at("Price / subscription €", idx) || 0;
      const arpuP = at("Price / subscription €", idx - 1) || 0;
      return {
        devices: dev, devicesDelta: dev - devP,
        mrr, mrrDelta: mrr - mrrP,
        arpu, arpuDelta: arpu - arpuP
      };
    }
  },
  methods: {
    ...mapActions("axios_mod", ["simpleFetch"]),
    range() {
      const now = new Date();
      const from = new Date(now.getFullYear(), now.getMonth() - this.rangeMonths, 1);
      const to = new Date(now.getFullYear(), now.getMonth() + 13, 1);
      const iso = d => `${d.getFullYear()}-${d.getMonth() + 1}-1`;
      return [iso(from), iso(to)];
    },
    monthForIndex(i) {
      const now = new Date();
      const d = new Date(now.getFullYear(), now.getMonth() - this.rangeMonths + i, 1);
      return `${d.getFullYear()}-${d.getMonth() + 1}-1`;
    },
    setRange(months) { this.rangeMonths = months; this.fetchData(); },
    fetchData() {
      const [from, to] = this.range();
      const b = `, brand: "${this.brand}"`;
      const data = {
        query: `query {
          SubscriptionSeries(from: "${from}", to: "${to}"${b})
          SubscriptionBridge(from: "${from}", to: "${to}"${b})
          SubscriptionArrReconciliation(brand: "${this.brand}")
        }`
      };
      this.isLoading = true;
      this.simpleFetch({ data, this_ref: this })
        .then(res => {
          if (!res) return;
          this.series = JSON.parse(res.SubscriptionSeries);
          this.bridge = JSON.parse(res.SubscriptionBridge);
          this.recon = JSON.parse(res.SubscriptionArrReconciliation);
        })
        .finally(() => {
          this.isLoading = false;
          this.$nextTick(() => {
            this.renderDevChart();
            this.renderMrrChart();
            this.renderPriceChart();
            this.renderBridgeChart();
            this.renderSalesChart();
          });
        });
    },
    openMovement(index, kind) {
      const fp = this.series.meta && this.series.meta.firstProjectedIndex;
      if (fp != null && index >= fp) return; // projected month — no actuals to drill into
      const month = this.monthForIndex(index);
      const kindArg = kind ? `, kind: "${kind}"` : "";
      this.modalTitle = `${this.series.labels[index] || month} · ${kind ? kind.replace("_", " ") : "all changes"}`;
      this.showModal = true;
      this.modalLoading = true;
      this.modalRows = [];
      this.simpleFetch({
        data: { query: `query { SubscriptionMovementCustomers(month: "${month}"${kindArg}, brand: "${this.brand}") }` },
        this_ref: this
      })
        .then(res => { if (res) this.modalRows = JSON.parse(res.SubscriptionMovementCustomers); })
        .finally(() => { this.modalLoading = false; });
    },
    openReconYear(year) {
      this.reconYear = year;
      this.showRecon = true;
      this.reconLoading = true;
      this.reconRows = [];
      this.simpleFetch({
        data: { query: `query { SubscriptionArrReconciliationCustomers(year: ${year}, brand: "${this.brand}") }` },
        this_ref: this
      })
        .then(res => { if (res) this.reconRows = JSON.parse(res.SubscriptionArrReconciliationCustomers); })
        .finally(() => { this.reconLoading = false; });
    },
    sortRows(rows, sort) {
      const k = sort.key, d = sort.dir;
      return rows.slice().sort((a, b) => {
        let va = a[k], vb = b[k];
        if (typeof va === "string" || typeof vb === "string") {
          return String(va || "").localeCompare(String(vb || "")) * d;
        }
        return ((va || 0) - (vb || 0)) * d;
      });
    },
    sortModal(key) { this.applySort(this.modalSort, key); },
    sortRecon(key) { this.applySort(this.reconSort, key); },
    applySort(sort, key) {
      if (sort.key === key) sort.dir = -sort.dir;
      else { sort.key = key; sort.dir = -1; }
    },
    sortArrow(key, which) {
      const s = which === "recon" ? this.reconSort : this.modalSort;
      return s.key === key ? (s.dir === -1 ? "▾" : "▴") : "";
    },
    baseOptions(yAxes, stackedX) {
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          xAxes: [{ stacked: !!stackedX, gridLines: { display: false } }],
          yAxes: yAxes
        },
        legend: { display: true, position: "bottom", labels: { boxWidth: 12, padding: 12 } },
        tooltips: { mode: "index", intersect: false },
        onHover: (e, els) => { e.target.style.cursor = els.length ? "pointer" : "default"; }
      };
    },
    eurAxis(id, pos, extra) {
      return Object.assign({ id, position: pos, ticks: { callback: v => "€" + this.short(v) } }, extra || {});
    },
    renderDevChart() {
      if (!this.$refs.devChart) return;
      if (this.charts.dev) this.charts.dev.destroy();
      const want = ["Subscriptions", "Subscriptions (projected)", "Billed IMEIs", "In fleet portal"];
      const datasets = this.series.datasets.filter(d => want.includes(d.label));
      const opts = this.baseOptions([{ id: "y", position: "left", stacked: true, ticks: { beginAtZero: true } }], true);
      opts.onClick = (evt) => {
        const pts = this.charts.dev.getElementsAtEventForMode(evt, "index", { intersect: false }, false);
        if (pts && pts.length) this.openMovement(pts[0]._index, null);
      };
      this.charts.dev = new Chart(this.$refs.devChart.getContext("2d"), {
        type: "bar", data: { labels: this.series.labels, datasets }, options: opts
      });
    },
    renderMrrChart() {
      if (!this.$refs.mrrChart) return;
      if (this.charts.mrr) this.charts.mrr.destroy();
      const wanted = ["MRR / month €", "MRR / month € (projected)", "Invoiced only €", "Price / subscription €"];
      const datasets = this.series.datasets.filter(d => wanted.includes(d.label));
      this.charts.mrr = new Chart(this.$refs.mrrChart.getContext("2d"), {
        type: "line", data: { labels: this.series.labels, datasets },
        options: this.baseOptions([
          this.eurAxis("y1", "left"),
          this.eurAxis("y2", "right", { gridLines: { drawOnChartArea: false }, ticks: { callback: v => "€" + v } })
        ])
      });
    },
    renderPriceChart() {
      if (!this.$refs.priceChart) return;
      if (this.charts.price) this.charts.price.destroy();
      const arpuDs = this.series.datasets.find(d => d.label === "Price / subscription €");
      const arpu = arpuDs ? arpuDs.data : [];
      const mom = arpu.map((v, i) => (i === 0 || v == null || arpu[i - 1] == null) ? null : +(v - arpu[i - 1]).toFixed(2));
      this.charts.price = new Chart(this.$refs.priceChart.getContext("2d"), {
        type: "bar",
        data: {
          labels: this.series.labels,
          datasets: [
            { label: "MoM change €", type: "bar", yAxisID: "y", data: mom,
              backgroundColor: mom.map(v => (v == null ? "rgba(0,0,0,0)" : v >= 0 ? "rgba(72,187,120,0.7)" : "rgba(237,137,54,0.75)")) },
            { label: "Price / subscription €", type: "line", yAxisID: "y1", fill: false,
              borderColor: "rgb(159, 122, 234)", backgroundColor: "transparent", data: arpu }
          ]
        },
        options: this.baseOptions([
          { id: "y", position: "left", ticks: { callback: v => "€" + v } },
          { id: "y1", position: "right", gridLines: { drawOnChartArea: false }, ticks: { callback: v => "€" + v } }
        ])
      });
    },
    renderBridgeChart() {
      if (!this.$refs.bridgeChart) return;
      if (this.charts.bridge) this.charts.bridge.destroy();
      const opts = this.baseOptions([
        this.eurAxis("y", "left", { stacked: true }),
        { id: "y1", position: "right", stacked: false, gridLines: { drawOnChartArea: false }, ticks: { callback: v => v + "%", beginAtZero: true } }
      ], true);
      opts.tooltips.callbacks = {
        label: (item, data) => {
          const ds = data.datasets[item.datasetIndex];
          return ds.yAxisID === "y1"
            ? `${ds.label}: ${item.yLabel}%`
            : `${ds.label}: €${Number(item.yLabel).toLocaleString()}`;
        }
      };
      const clickable = ["New logo", "Reactivation", "Expansion", "Contraction", "Rate Δ", "Churned", "Other"];
      const kindMap = {
        "New logo": "new_logo", "Reactivation": "reactivation", "Expansion": "expansion",
        "Contraction": "contraction", "Rate Δ": "rate", "Churned": "churned", "Other": null
      };
      opts.onClick = (evt) => {
        const pts = this.charts.bridge.getElementsAtEventForMode(evt, "point", { intersect: true }, false);
        if (!pts || !pts.length) return;
        const ds = this.bridge.mrr.datasets[pts[0]._datasetIndex];
        if (!ds || !clickable.includes(ds.label)) return;
        this.openMovement(pts[0]._index, kindMap[ds.label]);
      };
      this.charts.bridge = new Chart(this.$refs.bridgeChart.getContext("2d"), {
        type: "bar", data: { labels: this.bridge.labels, datasets: this.bridge.mrr.datasets }, options: opts
      });
    },
    renderSalesChart() {
      if (!this.$refs.salesChart) return;
      if (this.charts.sales) this.charts.sales.destroy();
      const opts = this.baseOptions([{ id: "y", position: "left", stacked: true }], true);
      this.charts.sales = new Chart(this.$refs.salesChart.getContext("2d"), {
        type: "bar", data: { labels: this.bridge.labels, datasets: this.bridge.counts.datasets }, options: opts
      });
    },
    short(v) {
      const n = Math.abs(v);
      if (n >= 1e6) return (v / 1e6).toFixed(1) + "M";
      if (n >= 1e3) return (v / 1e3).toFixed(0) + "k";
      return "" + Math.round(v);
    },
    fmtInt(v) { return v == null ? "–" : Math.round(v).toLocaleString(); },
    fmtEur(v, dp) { return (v == null || isNaN(v)) ? "–" : "€" + Number(v).toLocaleString(undefined, { maximumFractionDigits: dp || 0 }); },
    fmtDelta(v, dp, eur) {
      if (v == null || isNaN(v)) return "–";
      return (v >= 0 ? "+" : "−") + (eur ? "€" : "") + Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: dp || 0 });
    },
    deltaClass(v) { return { up: v > 0, down: v < 0 }; }
  },
  mounted() { this.fetchData(); },
  beforeDestroy() { Object.values(this.charts).forEach(c => c && c.destroy()); }
};
</script>

<style scoped>
.chart-wrapper-page { padding: 0; background-color: transparent; }

.beta-banner {
  display: flex; align-items: center; gap: 10px;
  margin: 0 0 14px; padding: 10px 16px;
  background: #fffaf0; border: 1px solid #feebc8; border-radius: 6px;
  font-size: 13px; line-height: 1.5; color: #7b341e;
}
.beta-tag {
  flex-shrink: 0; padding: 2px 9px; border-radius: 10px;
  background: #dd6b20; color: white; font-size: 11px; font-weight: 700;
  letter-spacing: 0.04em;
}

.click-hint {
  display: flex; align-items: flex-start; gap: 8px;
  margin: 0 0 20px; padding: 10px 16px;
  background: #ebf8ff; border-radius: 4px;
  font-size: 12.5px; line-height: 1.5; color: #2c5282;
}
.hint-icon { font-size: 15px; line-height: 1.4; }

.intro {
  margin: 0 0 20px; padding: 14px 16px; background: #f7fafc;
  border-left: 3px solid #5469d4; border-radius: 4px;
  font-size: 13px; line-height: 1.6; color: #4a5568;
}
.intro code, .explain code, .chart-hint code { background: #edf2f7; padding: 1px 4px; border-radius: 3px; font-size: 12px; }

.stat-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)); gap: 16px; margin-bottom: 24px; }
.stat-card { background: white; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px 18px; display: flex; flex-direction: column; gap: 4px; }
.stat-label { font-size: 12px; text-transform: uppercase; letter-spacing: 0.03em; color: #a0aec0; }
.stat-value { font-size: 24px; font-weight: 700; color: #2d3748; }
.stat-delta { font-size: 12px; color: #a0aec0; }
.stat-delta.up, .num.up { color: #38a169; }
.stat-delta.down, .num.down { color: #e53e3e; }
.stat-sub { font-size: 11px; color: #a0aec0; line-height: 1.4; }

.chart-card { background: white; padding: 20px; }
.chart-card + .chart-card { margin-top: 24px; border-top: 1px solid #e2e8f0; }
.chart-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; flex-wrap: wrap; gap: 16px; }
.chart-title { font-size: 18px; font-weight: 600; color: #2d3748; margin: 0; }

.explain {
  font-size: 12.5px; line-height: 1.6; color: #4a5568;
  background: #f9fafb; border: 1px solid #edf2f7; border-radius: 6px;
  padding: 10px 14px; margin-bottom: 16px;
}
.explain ul { margin: 6px 0 0; padding-left: 18px; }
.explain li { margin: 2px 0; }

.year-filters { display: flex; gap: 8px; background: #f7fafc; padding: 4px; border-radius: 8px; }
.year-btn { padding: 8px 20px; border: none; background: transparent; color: #4a5568; font-size: 14px; font-weight: 500; border-radius: 6px; cursor: pointer; min-width: 64px; }
.year-btn:hover { background-color: #edf2f7; }
.year-btn.active { background-color: #5469d4; color: white; }

.chart-container { height: 420px; position: relative; }
.loading-state { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; color: #718096; }
.loading-state.small { height: 80px; }
.loading-state .spinner { font-size: 28px; margin-bottom: 8px; color: #5469d4; }
.chart-hint { margin: 10px 0 0; font-size: 12px; color: #a0aec0; }

.recon-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.recon-table th { text-align: left; font-size: 11px; text-transform: uppercase; color: #a0aec0; border-bottom: 2px solid #e2e8f0; padding: 8px 10px; }
.recon-table td { padding: 10px; border-bottom: 1px solid #edf2f7; }
.recon-table .muted { color: #a0aec0; font-size: 11px; }

.customer-list-table { width: 100%; border-collapse: collapse; }
.customer-list-table th { text-align: left; font-size: 11px; text-transform: uppercase; color: #a0aec0; border-bottom: 2px solid #e2e8f0; padding: 8px 12px; cursor: pointer; user-select: none; }
.customer-list-table th:hover { color: #4a5568; }
.customer-list-table th .sort { color: #5469d4; }

.brand-bar { display: flex; align-items: center; gap: 10px; margin-bottom: 18px; }
.brand-bar label { font-size: 12px; text-transform: uppercase; letter-spacing: 0.03em; color: #a0aec0; }
.brand-bar select { padding: 6px 10px; border: 1px solid #e2e8f0; border-radius: 6px; font-size: 13px; color: #2d3748; background: white; }

.customer-list-table th.why { cursor: default; }
.customer-list-table td.why { font-size: 12px; color: #718096; max-width: 420px; }
.recon-row { cursor: pointer; }
.recon-row:hover { background-color: #f7fafc; }
.tag { display: inline-block; margin-left: 6px; padding: 1px 7px; border-radius: 9px; font-size: 10px; font-weight: 600; text-transform: uppercase; background: #edf2f7; color: #718096; }
.customer-list-table th.num, .customer-list-table td.num { text-align: right; }
.customer-list-table td { padding: 9px 12px; border-bottom: 1px solid #edf2f7; font-size: 13px; }
.customer-list-table tbody tr:hover { background-color: #f7fafc; }
.kind-badge { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; color: white; text-transform: capitalize; }
.kind-badge.new_logo { background: #38a169; }
.kind-badge.reactivation { background: #38b2ac; }
.kind-badge.expansion { background: #4299e1; }
.kind-badge.contraction { background: #ed8936; }
.kind-badge.churned { background: #f56565; }
.kind-badge.rate { background: #9f7aea; }
.modal-count { color: #a0aec0; font-weight: 400; font-size: 14px; }
.modal-loading, .modal-empty { display: flex; flex-direction: column; align-items: center; padding: 40px 0; color: #718096; }

@media (max-width: 768px) {
  .chart-card { padding: 16px; }
  .chart-header { flex-direction: column; align-items: flex-start; }
  .year-filters { width: 100%; }
  .year-btn { flex: 1; }
  .chart-container { height: 320px; }
  .recon-table { display: block; overflow-x: auto; }
}
</style>
